// src/pages/support/TicketDetails.jsx
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  MessageSquare,
  Clock,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Paperclip,
  Send,
  Edit,
  Trash2,
  Download,
  Calendar,
  User,
  Tag as TagIcon,
  FileText,
  Loader2,
  Shield,
  Timer,
  Package,
  Zap,
  TrendingUp,
  TrendingDown,
  Lock,
  Unlock,
  Eye,
  Bell,
  Activity,
  BarChart3,
  Info,
  ChevronRight,
  Circle,
  Square,
  AlertCircle as AlertCircleIcon,
} from 'lucide-react';
import ticketService from '../../services/ticketService';
import useAuth from '../../hooks/useAuth';
import { formatInIST } from '../../utils/dateHelpers';
import { saveTicketToSheet } from '../../services/googleSheetsService';

const PRIORITY_COLORS = {
  P0: 'bg-red-100 text-red-800 border-red-300',
  P1: 'bg-orange-100 text-orange-800 border-orange-300',
  P2: 'bg-yellow-100 text-yellow-800 border-yellow-300',
  P3: 'bg-blue-100 text-blue-800 border-blue-300',
  P4: 'bg-gray-100 text-gray-800 border-gray-300',
};

const SEVERITY_COLORS = {
  CRITICAL: 'bg-red-100 text-red-800',
  HIGH: 'bg-orange-100 text-orange-800',
  MEDIUM: 'bg-yellow-100 text-yellow-800',
  LOW: 'bg-blue-100 text-blue-800',
};

const STATUS_COLORS = {
  OPEN: 'bg-blue-100 text-blue-800',
  IN_PROGRESS: 'bg-purple-100 text-purple-800',
  WAITING_ON_REPORTER: 'bg-yellow-100 text-yellow-800',
  WAITING_ON_THIRD_PARTY: 'bg-amber-100 text-amber-800',
  RESOLVED: 'bg-green-100 text-green-800',
  CLOSED: 'bg-gray-100 text-gray-800',
};

const EVENT_TYPE_ICONS = {
  TICKET_CREATED: Circle,
  COMMENT_ADDED: MessageSquare,
  PRIORITY_CHANGED: TrendingUp,
  STATUS_CHANGED: Activity,
  ATTACHMENT_ADDED: Paperclip,
  ASSIGNMENT_CHANGED: User,
  SLA_BREACHED: AlertTriangle,
};

const TicketDetails = () => {
  const { publicId } = useParams();
  const navigate = useNavigate();
  const { getMerchantId } = useAuth();
  const merchantId = getMerchantId();

  const [ticket, setTicket] = useState(null);
  const [messages, setMessages] = useState([]);
  const [timeline, setTimeline] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('messages');
  const [showCloseModal, setShowCloseModal] = useState(false);
  const [showReopenModal, setShowReopenModal] = useState(false);
  const [closingTicket, setClosingTicket] = useState(false);
  const [reopeningTicket, setReopeningTicket] = useState(false);

  // Message input
  const [messageText, setMessageText] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const messagesEndRef = useRef(null);

  // Close/Reopen form
  const [closeReason, setCloseReason] = useState('');
  const [resolutionNote, setResolutionNote] = useState('');
  const [reopenReason, setReopenReason] = useState('');

  const fetchTicket = useCallback(async () => {
    if (!publicId || !merchantId) return;

    try {
      const data = await ticketService.getTicket(publicId, merchantId);
      setTicket(data);
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || 'Failed to load ticket');
    }
  }, [publicId, merchantId]);

  const fetchMessages = useCallback(async () => {
    if (!publicId) return;

    try {
      const data = await ticketService.listTicketMessages(publicId, 100, 0);
      setMessages(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Failed to load messages:', err);
    }
  }, [publicId]);

  const fetchTimeline = useCallback(async () => {
    if (!publicId) return;

    try {
      const response = await ticketService.getTicketTimeline(publicId, 100, 0);
      setTimeline(response.data || []);
    } catch (err) {
      console.error('Failed to load timeline:', err);
    }
  }, [publicId]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchTicket(), fetchMessages(), fetchTimeline()]);
      setLoading(false);
    };
    loadData();
  }, [fetchTicket, fetchMessages, fetchTimeline]);

  // Auto-refresh every 30 seconds
  useEffect(() => {
    if (!publicId) return;
    const interval = setInterval(() => {
      fetchTicket();
      fetchMessages();
    }, 30000);
    return () => clearInterval(interval);
  }, [publicId, fetchTicket, fetchMessages]);

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSendMessage = async () => {
    if (!messageText.trim() && selectedFiles.length === 0) return;
    if (!publicId || !merchantId) return;

    setSendingMessage(true);
    try {
      // API requires message field to be non-empty
      // If user only attaches files without message, use a default message
      const messageContent = messageText.trim() || (selectedFiles.length > 0 ? 'Attached files' : '');
      
      if (!messageContent) {
        alert('Please enter a message or attach files');
        setSendingMessage(false);
        return;
      }

      const messageData = {
        message: messageContent,
        body_format: 'plaintext',
        actor_type: 'merchant',
        actor_id: merchantId,
      };

      await ticketService.addTicketMessage(publicId, messageData, selectedFiles);
      setMessageText('');
      setSelectedFiles([]);
      await fetchMessages();
      await fetchTicket();
      await fetchTimeline();
    } catch (err) {
      console.error('Failed to send message:', err);
      const errorMessage = err?.response?.data?.message || err?.message || 'Failed to send message';
      alert(errorMessage);
    } finally {
      setSendingMessage(false);
    }
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    setSelectedFiles((prev) => [...prev, ...files]);
  };

  const handleRemoveFile = (index) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleDownloadAttachment = async (attachmentId, fileName) => {
    try {
      const response = await ticketService.downloadTicketAttachment(publicId, attachmentId);
      window.open(response.download_url, '_blank');
    } catch (err) {
      console.error('Failed to download attachment:', err);
      alert('Failed to download attachment');
    }
  };

  const handleDownloadMessageAttachment = async (sequenceNumber, attachmentId) => {
    try {
      const response = await ticketService.downloadMessageAttachment(
        publicId,
        sequenceNumber,
        attachmentId
      );
      window.open(response.download_url, '_blank');
    } catch (err) {
      console.error('Failed to download attachment:', err);
      alert('Failed to download attachment');
    }
  };

  const handleCloseTicket = async () => {
    if (!closeReason.trim()) {
      alert('Please provide a closure reason');
      return;
    }
    if (!publicId || !merchantId) return;

    setClosingTicket(true);
    try {
      const closureData = {
        closure_reason: closeReason.trim(),
        resolution_note: resolutionNote.trim() || undefined,
        closed_by: {
          actor_type: 'merchant',
          actor_id: merchantId,
        },
      };

      await ticketService.closeTicket(publicId, closureData);
      
      // Update Google Sheets with status change
      try {
        const updatedTicket = await ticketService.getTicket(publicId, merchantId);
        await saveTicketToSheet(updatedTicket, 'update');
        console.log('✅ Ticket status update saved to Google Sheets');
      } catch (sheetError) {
        console.warn('⚠️ Failed to update ticket in Google Sheets (non-critical):', sheetError);
      }
      
      setShowCloseModal(false);
      setCloseReason('');
      setResolutionNote('');
      await fetchTicket();
      await fetchTimeline();
    } catch (err) {
      console.error('Failed to close ticket:', err);
      alert(err?.response?.data?.message || 'Failed to close ticket');
    } finally {
      setClosingTicket(false);
    }
  };

  const handleReopenTicket = async () => {
    if (!reopenReason.trim()) {
      alert('Please provide a reopen reason');
      return;
    }
    if (!publicId || !merchantId) return;

    setReopeningTicket(true);
    try {
      const reopenData = {
        reopen_reason: reopenReason.trim(),
        reopened_by: {
          actor_type: 'merchant',
          actor_id: merchantId,
        },
      };

      await ticketService.reopenTicket(publicId, reopenData);
      
      // Update Google Sheets with status change
      try {
        const updatedTicket = await ticketService.getTicket(publicId, merchantId);
        await saveTicketToSheet(updatedTicket, 'update');
        console.log('✅ Ticket status update saved to Google Sheets');
      } catch (sheetError) {
        console.warn('⚠️ Failed to update ticket in Google Sheets (non-critical):', sheetError);
      }
      
      setShowReopenModal(false);
      setReopenReason('');
      await fetchTicket();
      await fetchTimeline();
    } catch (err) {
      console.error('Failed to reopen ticket:', err);
      alert(err?.response?.data?.message || 'Failed to reopen ticket');
    } finally {
      setReopeningTicket(false);
    }
  };

  const isSLABreached = (ticket) => {
    const sla = ticket?.sla_info || {};
    return sla.assignment_breached || sla.first_response_breached || sla.resolution_breached;
  };

  const formatPriority = (priority) => {
    const p = priority?.final_value || priority?.suggested || 'P3';
    return p.toUpperCase();
  };

  const formatSeverity = (severity) => {
    const s = severity?.final_value || severity?.suggested || 'MEDIUM';
    return s;
  };

  // Calculate time remaining for SLA
  const getSLATimeRemaining = (dueAt) => {
    if (!dueAt) return null;
    const due = new Date(dueAt);
    const now = new Date();
    const diff = due - now;
    if (diff <= 0) return { overdue: true, minutes: Math.abs(Math.floor(diff / 60000)) };
    const hours = Math.floor(diff / 3600000);
    const minutes = Math.floor((diff % 3600000) / 60000);
    return { overdue: false, hours, minutes };
  };

  // Format timeline event description
  const formatTimelineEvent = (event) => {
    if (event.description) return event.description;
    
    switch (event.type) {
      case 'TICKET_CREATED':
        return `Ticket created`;
      case 'COMMENT_ADDED':
        return `${event.actor?.actor_id || 'User'} added a comment`;
      case 'PRIORITY_CHANGED':
        const priorityPayload = event.payload;
        return `Priority changed from ${priorityPayload?.previousPriority || 'N/A'} to ${priorityPayload?.newPriority || 'N/A'}`;
      case 'STATUS_CHANGED':
        return `Status changed`;
      case 'ATTACHMENT_ADDED':
        return `${event.actor?.actor_id || 'User'} added attachment`;
      default:
        return event.type?.replace(/_/g, ' ') || 'Event';
    }
  };

  const getEventIcon = (eventType) => {
    const IconComponent = EVENT_TYPE_ICONS[eventType] || Circle;
    return IconComponent;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-16 h-16 text-indigo-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600 font-medium text-lg">Loading ticket details...</p>
        </div>
      </div>
    );
  }

  if (error || !ticket) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl p-8 shadow-xl max-w-md w-full text-center">
          <XCircle className="w-20 h-20 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Ticket Not Found</h2>
          <p className="text-gray-600 mb-6">{error || 'The ticket you are looking for does not exist.'}</p>
          <button
            onClick={() => navigate('/support/tickets')}
            className="px-6 py-3 bg-indigo-600 text-white rounded-xl font-semibold hover:bg-indigo-700 transition-colors shadow-lg"
          >
            Back to Tickets
          </button>
        </div>
      </div>
    );
  }

  const sla = ticket.sla_info || {};
  const priority = formatPriority(ticket.priority);
  const severity = formatSeverity(ticket.severity);
  const isBreached = isSLABreached(ticket);
  const assignmentTime = getSLATimeRemaining(sla.assignment_due_at);
  const firstResponseTime = getSLATimeRemaining(sla.first_response_due_at);
  const resolutionTime = getSLATimeRemaining(sla.resolution_due_at);

  return (
    <div className="w-full min-h-[calc(100vh-88px)] bg-gradient-to-br from-slate-50 via-gray-50 to-slate-50 overflow-x-hidden">
      <div className="w-full h-full px-2 sm:px-3 lg:px-2 py-2 md:py-2 space-y-3 md:space-y-4 max-w-full">
        {/* Back Button */}
        <button
          onClick={() => navigate('/support/tickets')}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors font-medium text-sm sm:text-base mb-1 sm:mb-2"
        >
          <ArrowLeft className="w-4 h-4 sm:w-5 sm:h-5" />
          <span className="hidden sm:inline">Back to Tickets</span>
          <span className="sm:hidden">Back</span>
        </button>

        {/* Enhanced Header Card */}
        <div className="bg-white rounded-lg sm:rounded-xl md:rounded-2xl shadow-md border border-gray-200 overflow-hidden">
          <div className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 p-3 sm:p-4 md:p-6 text-white">
            <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-3 sm:gap-4">
              <div className="flex-1 min-w-0">
                <div className="flex flex-col sm:flex-row sm:items-start gap-3 sm:gap-4 mb-3 sm:mb-4">
                  <div className="flex-1 min-w-0">
                    <h1 className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-bold mb-1 sm:mb-2 break-words">{ticket.title}</h1>
                    <p className="text-indigo-100 font-mono text-xs sm:text-sm">#{ticket.public_id}</p>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span
                      className={`inline-flex items-center px-2 sm:px-3 md:px-4 py-1 sm:py-1.5 md:py-2 rounded-lg sm:rounded-xl text-xs sm:text-sm font-bold border-2 backdrop-blur-sm bg-white/20 ${
                        PRIORITY_COLORS[priority] || PRIORITY_COLORS.P3
                      }`}
                    >
                      {priority}
                      {ticket.priority?.final_value !== ticket.priority?.suggested && (
                        <TrendingUp className="w-3 h-3 sm:w-4 sm:h-4 ml-0.5 sm:ml-1" />
                      )}
                    </span>
                    <span
                      className={`inline-flex items-center px-2 sm:px-3 md:px-4 py-1 sm:py-1.5 md:py-2 rounded-lg sm:rounded-xl text-xs sm:text-sm font-semibold backdrop-blur-sm bg-white/20 ${
                        STATUS_COLORS[ticket.status] || 'bg-gray-100 text-gray-800'
                      }`}
                    >
                      {ticket.status?.replace(/_/g, ' ') || ''}
                    </span>
                    {ticket.status === 'CLOSED' ? (
                      <button
                        onClick={() => setShowReopenModal(true)}
                        className="inline-flex items-center gap-1.5 sm:gap-2 px-2 sm:px-3 md:px-4 py-1 sm:py-1.5 md:py-2 bg-white/20 hover:bg-white/30 rounded-lg sm:rounded-xl text-xs sm:text-sm font-semibold backdrop-blur-sm transition-colors"
                      >
                        <Unlock className="w-3 h-3 sm:w-4 sm:h-4" />
                        <span className="hidden sm:inline">Reopen</span>
                      </button>
                    ) : (
                      <button
                        onClick={() => setShowCloseModal(true)}
                        className="inline-flex items-center gap-1.5 sm:gap-2 px-2 sm:px-3 md:px-4 py-1 sm:py-1.5 md:py-2 bg-white/20 hover:bg-white/30 rounded-lg sm:rounded-xl text-xs sm:text-sm font-semibold backdrop-blur-sm transition-colors"
                      >
                        <Lock className="w-3 h-3 sm:w-4 sm:h-4" />
                        <span className="hidden sm:inline">Close</span>
                      </button>
                    )}
                  </div>
                </div>

                {/* Tags */}
                {ticket.tags && ticket.tags.length > 0 && (
                  <div className="flex items-center gap-2 flex-wrap mb-3 sm:mb-4">
                    {ticket.tags.map((tag, idx) => (
                      <span
                        key={idx}
                        className="inline-flex items-center gap-1 sm:gap-1.5 px-2 sm:px-3 py-0.5 sm:py-1 bg-white/20 backdrop-blur-sm rounded text-xs sm:text-sm font-medium"
                      >
                        <TagIcon className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
                        {tag}
                      </span>
                    ))}
                  </div>
                )}

                {/* Custom Fields - Order & AWB */}
                {ticket.custom_fields && (
                  <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
                    {ticket.custom_fields.order_reference_number && (
                      <div className="flex items-center gap-1.5 sm:gap-2 px-2 sm:px-3 py-1 sm:py-1.5 bg-white/20 backdrop-blur-sm rounded text-xs sm:text-sm font-medium">
                        <Package className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                        <span className="truncate max-w-[150px] sm:max-w-none">Order: {ticket.custom_fields.order_reference_number}</span>
                      </div>
                    )}
                    {ticket.custom_fields.awb_number && (
                      <div className="flex items-center gap-1.5 sm:gap-2 px-2 sm:px-3 py-1 sm:py-1.5 bg-white/20 backdrop-blur-sm rounded text-xs sm:text-sm font-medium">
                        <FileText className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                        <span className="truncate max-w-[150px] sm:max-w-none">AWB: {ticket.custom_fields.awb_number}</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Ticket Details Section */}
          <div className="p-3 sm:p-4 md:p-6">
            <p className="text-gray-700 mb-4 sm:mb-6 whitespace-pre-wrap leading-relaxed text-sm sm:text-base">{ticket.description}</p>

            {/* Enhanced Info Grid */}
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2 sm:gap-3 md:gap-4 mb-4 sm:mb-6">
              <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg sm:rounded-xl p-2 sm:p-3 md:p-4">
                <p className="text-[10px] sm:text-xs font-semibold text-blue-600 mb-1">Category</p>
                <p className="text-xs sm:text-sm font-bold text-gray-900">
                  {ticket.category?.replace(/_/g, ' ') || '-'}
                </p>
                {ticket.subcategory && (
                  <p className="text-[10px] sm:text-xs text-gray-600 mt-1">{ticket.subcategory.replace(/_/g, ' ')}</p>
                )}
              </div>
              <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg sm:rounded-xl p-2 sm:p-3 md:p-4">
                <p className="text-[10px] sm:text-xs font-semibold text-purple-600 mb-1">Severity</p>
                <span
                  className={`inline-flex items-center px-1.5 sm:px-2.5 py-0.5 sm:py-1 rounded text-[10px] sm:text-xs font-bold ${
                    SEVERITY_COLORS[severity] || SEVERITY_COLORS.MEDIUM
                  }`}
                >
                  {severity}
                </span>
              </div>
              <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg sm:rounded-xl p-2 sm:p-3 md:p-4">
                <p className="text-[10px] sm:text-xs font-semibold text-green-600 mb-1">Created</p>
                <p className="text-xs sm:text-sm font-bold text-gray-900">
                  {ticket.created_at ? formatInIST(ticket.created_at) : '-'}
                </p>
              </div>
              <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-lg sm:rounded-xl p-2 sm:p-3 md:p-4">
                <p className="text-[10px] sm:text-xs font-semibold text-orange-600 mb-1">Last Updated</p>
                <p className="text-xs sm:text-sm font-bold text-gray-900">
                  {ticket.updated_at ? formatInIST(ticket.updated_at) : '-'}
                </p>
              </div>
              <div className="bg-gradient-to-br from-indigo-50 to-indigo-100 rounded-lg sm:rounded-xl p-2 sm:p-3 md:p-4">
                <p className="text-[10px] sm:text-xs font-semibold text-indigo-600 mb-1">Messages</p>
                <p className="text-lg sm:text-xl md:text-2xl font-bold text-gray-900">
                  {ticket.counters?.message_count || 0}
                </p>
                {ticket.counters?.merchant_unread_count > 0 && (
                  <p className="text-[10px] sm:text-xs text-indigo-600 font-semibold mt-1">
                    {ticket.counters.merchant_unread_count} unread
                  </p>
                )}
              </div>
              <div className="bg-gradient-to-br from-pink-50 to-pink-100 rounded-lg sm:rounded-xl p-2 sm:p-3 md:p-4">
                <p className="text-[10px] sm:text-xs font-semibold text-pink-600 mb-1">Attachments</p>
                <p className="text-lg sm:text-xl md:text-2xl font-bold text-gray-900">
                  {ticket.counters?.attachment_count || 0}
                </p>
              </div>
            </div>

            {/* Enhanced SLA Status */}
            <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg sm:rounded-xl md:rounded-2xl p-3 sm:p-4 md:p-6 border-2 border-gray-200">
              <div className="flex items-center justify-between mb-3 sm:mb-4">
                <div className="flex items-center gap-1.5 sm:gap-2">
                  <Shield className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 text-indigo-600" />
                  <h3 className="text-sm sm:text-base md:text-lg font-bold text-gray-900">SLA Status</h3>
                </div>
                {isBreached ? (
                  <div className="flex items-center gap-1.5 sm:gap-2 px-2 sm:px-3 md:px-4 py-1 sm:py-1.5 md:py-2 bg-red-100 rounded-lg sm:rounded-xl">
                    <AlertTriangle className="w-4 h-4 sm:w-5 sm:h-5 text-red-600" />
                    <span className="text-xs sm:text-sm font-bold text-red-600">SLA BREACHED</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-1.5 sm:gap-2 px-2 sm:px-3 md:px-4 py-1 sm:py-1.5 md:py-2 bg-green-100 rounded-lg sm:rounded-xl">
                    <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-green-600" />
                    <span className="text-xs sm:text-sm font-bold text-green-600">ON TRACK</span>
                  </div>
                )}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2 sm:gap-3 md:gap-4">
                {/* Assignment SLA */}
                <div className={`rounded-lg sm:rounded-xl p-2 sm:p-3 md:p-4 ${sla.assignment_breached ? 'bg-red-50 border-2 border-red-200' : 'bg-white border border-gray-200'}`}>
                  <div className="flex items-center justify-between mb-1.5 sm:mb-2">
                    <span className="text-xs sm:text-sm font-semibold text-gray-700">Assignment</span>
                    {sla.assignment_breached ? (
                      <AlertTriangle className="w-4 h-4 sm:w-5 sm:h-5 text-red-500" />
                    ) : sla.assignment_completed_at ? (
                      <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-green-500" />
                    ) : (
                      <Clock className="w-4 h-4 sm:w-5 sm:h-5 text-blue-500" />
                    )}
                  </div>
                  {sla.assignment_breached ? (
                    <div>
                      <p className="text-[10px] sm:text-xs text-red-600 font-semibold">
                        Breached by {sla.assignment_breach_minutes} minutes
                      </p>
                    </div>
                  ) : sla.assignment_completed_at ? (
                    <p className="text-[10px] sm:text-xs text-green-600 font-semibold">Completed</p>
                  ) : assignmentTime ? (
                    <div>
                      {assignmentTime.overdue ? (
                        <p className="text-[10px] sm:text-xs text-red-600 font-semibold">
                          Overdue {assignmentTime.minutes}m
                        </p>
                      ) : (
                        <p className="text-[10px] sm:text-xs text-blue-600 font-semibold">
                          {assignmentTime.hours}h {assignmentTime.minutes}m remaining
                        </p>
                      )}
                      <p className="text-[10px] sm:text-xs text-gray-500 mt-1">
                        Due: {sla.assignment_due_at ? formatInIST(sla.assignment_due_at) : 'N/A'}
                      </p>
                    </div>
                  ) : (
                    <p className="text-[10px] sm:text-xs text-gray-500">Not set</p>
                  )}
                </div>

                {/* First Response SLA */}
                <div className={`rounded-lg sm:rounded-xl p-2 sm:p-3 md:p-4 ${sla.first_response_breached ? 'bg-red-50 border-2 border-red-200' : 'bg-white border border-gray-200'}`}>
                  <div className="flex items-center justify-between mb-1.5 sm:mb-2">
                    <span className="text-xs sm:text-sm font-semibold text-gray-700">First Response</span>
                    {sla.first_response_breached ? (
                      <AlertTriangle className="w-4 h-4 sm:w-5 sm:h-5 text-red-500" />
                    ) : sla.first_response_completed_at ? (
                      <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-green-500" />
                    ) : (
                      <Clock className="w-4 h-4 sm:w-5 sm:h-5 text-blue-500" />
                    )}
                  </div>
                  {sla.first_response_breached ? (
                    <div>
                      <p className="text-[10px] sm:text-xs text-red-600 font-semibold">
                        Breached by {sla.first_response_breach_minutes} minutes
                      </p>
                    </div>
                  ) : sla.first_response_completed_at ? (
                    <p className="text-[10px] sm:text-xs text-green-600 font-semibold">Completed</p>
                  ) : firstResponseTime ? (
                    <div>
                      {firstResponseTime.overdue ? (
                        <p className="text-[10px] sm:text-xs text-red-600 font-semibold">
                          Overdue {firstResponseTime.minutes}m
                        </p>
                      ) : (
                        <p className="text-[10px] sm:text-xs text-blue-600 font-semibold">
                          {firstResponseTime.hours}h {firstResponseTime.minutes}m remaining
                        </p>
                      )}
                      <p className="text-[10px] sm:text-xs text-gray-500 mt-1">
                        Due: {sla.first_response_due_at ? formatInIST(sla.first_response_due_at) : 'N/A'}
                      </p>
                    </div>
                  ) : (
                    <p className="text-[10px] sm:text-xs text-gray-500">Not set</p>
                  )}
                </div>

                {/* Resolution SLA */}
                <div className={`rounded-lg sm:rounded-xl p-2 sm:p-3 md:p-4 ${sla.resolution_breached ? 'bg-red-50 border-2 border-red-200' : 'bg-white border border-gray-200'}`}>
                  <div className="flex items-center justify-between mb-1.5 sm:mb-2">
                    <span className="text-xs sm:text-sm font-semibold text-gray-700">Resolution</span>
                    {sla.resolution_breached ? (
                      <AlertTriangle className="w-4 h-4 sm:w-5 sm:h-5 text-red-500" />
                    ) : sla.resolution_completed_at ? (
                      <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-green-500" />
                    ) : (
                      <Clock className="w-4 h-4 sm:w-5 sm:h-5 text-blue-500" />
                    )}
                  </div>
                  {sla.resolution_breached ? (
                    <div>
                      <p className="text-[10px] sm:text-xs text-red-600 font-semibold">
                        Breached by {sla.resolution_breach_minutes} minutes
                      </p>
                    </div>
                  ) : sla.resolution_completed_at ? (
                    <p className="text-[10px] sm:text-xs text-green-600 font-semibold">Completed</p>
                  ) : resolutionTime ? (
                    <div>
                      {resolutionTime.overdue ? (
                        <p className="text-[10px] sm:text-xs text-red-600 font-semibold">
                          Overdue {resolutionTime.minutes}m
                        </p>
                      ) : (
                        <p className="text-[10px] sm:text-xs text-blue-600 font-semibold">
                          {resolutionTime.hours}h {resolutionTime.minutes}m remaining
                        </p>
                      )}
                      <p className="text-[10px] sm:text-xs text-gray-500 mt-1">
                        Due: {sla.resolution_due_at ? formatInIST(sla.resolution_due_at) : 'N/A'}
                      </p>
                    </div>
                  ) : (
                    <p className="text-[10px] sm:text-xs text-gray-500">Not set</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Enhanced Tabs */}
        <div className="bg-white rounded-lg sm:rounded-xl md:rounded-2xl shadow-md border border-gray-200 overflow-hidden">
          <div className="border-b-2 border-gray-200 bg-gradient-to-r from-gray-50 to-gray-100">
            <nav className="flex -mb-px overflow-x-auto scrollbar-hide">
              <button
                onClick={() => setActiveTab('messages')}
                className={`px-3 sm:px-5 md:px-8 py-3 sm:py-4 md:py-5 font-bold text-xs sm:text-sm border-b-4 transition-all whitespace-nowrap flex-shrink-0 ${
                  activeTab === 'messages'
                    ? 'border-indigo-600 text-indigo-600 bg-white'
                    : 'border-transparent text-gray-600 hover:text-gray-900 hover:bg-white/50'
                }`}
              >
                <div className="flex items-center gap-1.5 sm:gap-2">
                  <MessageSquare className="w-4 h-4 sm:w-5 sm:h-5" />
                  <span className="hidden sm:inline">Messages</span>
                  <span className="sm:hidden">Msgs</span>
                  {messages.length > 0 && (
                    <span className="px-1.5 sm:px-2 py-0.5 bg-indigo-100 text-indigo-800 text-[10px] sm:text-xs font-bold rounded-full">
                      {messages.length}
                    </span>
                  )}
                </div>
              </button>
              <button
                onClick={() => setActiveTab('timeline')}
                className={`px-3 sm:px-5 md:px-8 py-3 sm:py-4 md:py-5 font-bold text-xs sm:text-sm border-b-4 transition-all whitespace-nowrap flex-shrink-0 ${
                  activeTab === 'timeline'
                    ? 'border-indigo-600 text-indigo-600 bg-white'
                    : 'border-transparent text-gray-600 hover:text-gray-900 hover:bg-white/50'
                }`}
              >
                <div className="flex items-center gap-1.5 sm:gap-2">
                  <Clock className="w-4 h-4 sm:w-5 sm:h-5" />
                  Timeline
                  {timeline.length > 0 && (
                    <span className="px-1.5 sm:px-2 py-0.5 bg-purple-100 text-purple-800 text-[10px] sm:text-xs font-bold rounded-full">
                      {timeline.length}
                    </span>
                  )}
                </div>
              </button>
              <button
                onClick={() => setActiveTab('attachments')}
                className={`px-3 sm:px-5 md:px-8 py-3 sm:py-4 md:py-5 font-bold text-xs sm:text-sm border-b-4 transition-all whitespace-nowrap flex-shrink-0 ${
                  activeTab === 'attachments'
                    ? 'border-indigo-600 text-indigo-600 bg-white'
                    : 'border-transparent text-gray-600 hover:text-gray-900 hover:bg-white/50'
                }`}
              >
                <div className="flex items-center gap-1.5 sm:gap-2">
                  <Paperclip className="w-4 h-4 sm:w-5 sm:h-5" />
                  <span className="hidden sm:inline">Attachments</span>
                  <span className="sm:hidden">Files</span>
                  {(ticket.attachments?.length || 0) > 0 && (
                    <span className="px-1.5 sm:px-2 py-0.5 bg-pink-100 text-pink-800 text-[10px] sm:text-xs font-bold rounded-full">
                      {ticket.attachments?.length || 0}
                    </span>
                  )}
                </div>
              </button>
            </nav>
          </div>

          {/* Messages Tab */}
          {activeTab === 'messages' && (
            <div className="p-3 sm:p-4 md:p-6">
              <div className="space-y-3 sm:space-y-4 mb-4 sm:mb-6 max-h-[400px] sm:max-h-[500px] md:max-h-[600px] overflow-y-auto pr-1 sm:pr-2">
                {messages.length === 0 ? (
                  <div className="text-center py-16 text-gray-500">
                    <MessageSquare className="w-16 h-16 mx-auto mb-4 text-gray-400" />
                    <p className="font-semibold text-lg">No messages yet</p>
                    <p className="text-sm mt-2">Start the conversation!</p>
                  </div>
                ) : (
                  messages.map((message) => {
                    const isMerchant = message.author?.actor_type === 'merchant';
                    return (
                      <div
                        key={message.id || message.sequence_number}
                        className={`flex gap-4 ${isMerchant ? 'justify-end' : 'justify-start'}`}
                      >
                      <div
                        className={`max-w-full sm:max-w-2xl rounded-lg sm:rounded-xl md:rounded-2xl p-3 sm:p-4 md:p-5 shadow-md sm:shadow-lg ${
                          isMerchant
                            ? 'bg-gradient-to-br from-indigo-500 to-purple-600 text-white'
                            : 'bg-gradient-to-br from-gray-100 to-gray-200 text-gray-900'
                        }`}
                      >
                        <div className="flex items-center gap-2 sm:gap-3 mb-2 sm:mb-3">
                          <div className={`p-1.5 sm:p-2 rounded text-xs sm:text-sm ${isMerchant ? 'bg-white/20' : 'bg-indigo-100'}`}>
                            <User className={`w-3.5 h-3.5 sm:w-4 sm:h-4 ${isMerchant ? 'text-white' : 'text-indigo-600'}`} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <span className="font-bold text-xs sm:text-sm truncate">
                              {message.author?.name || message.author?.actor_id || 'Unknown'}
                            </span>
                            <span className={`text-[10px] sm:text-xs ml-1 sm:ml-2 ${isMerchant ? 'text-indigo-100' : 'text-gray-500'}`}>
                              {message.created_at ? formatInIST(message.created_at) : ''}
                            </span>
                          </div>
                          {message.sequence_number && (
                            <span className={`text-[10px] sm:text-xs font-mono flex-shrink-0 ${isMerchant ? 'text-indigo-100' : 'text-gray-500'}`}>
                              #{message.sequence_number}
                            </span>
                          )}
                        </div>
                        <p className="whitespace-pre-wrap leading-relaxed text-xs sm:text-sm md:text-base">{message.body}</p>
                          {message.attachments && message.attachments.length > 0 && (
                            <div className="mt-3 sm:mt-4 space-y-2 pt-3 sm:pt-4 border-t border-white/20">
                              {message.attachments.map((att) => (
                                <button
                                  key={att.attachment_id}
                                  onClick={() =>
                                    handleDownloadMessageAttachment(
                                      message.sequence_number,
                                      att.attachment_id
                                    )
                                  }
                                  className={`flex items-center gap-2 sm:gap-3 px-2 sm:px-3 md:px-4 py-1.5 sm:py-2 rounded text-xs sm:text-sm font-medium transition-all hover:scale-105 w-full ${
                                    isMerchant
                                      ? 'bg-white/20 hover:bg-white/30 text-white'
                                      : 'bg-indigo-50 hover:bg-indigo-100 text-indigo-700'
                                  }`}
                                >
                                  <Paperclip className="w-3.5 h-3.5 sm:w-4 sm:h-4 flex-shrink-0" />
                                  <span className="flex-1 text-left truncate">{att.file_name}</span>
                                  <Download className="w-3.5 h-3.5 sm:w-4 sm:h-4 flex-shrink-0" />
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Enhanced Message Input */}
              <div className="border-t-2 border-gray-200 pt-3 sm:pt-4 md:pt-6">
                {selectedFiles.length > 0 && (
                  <div className="mb-3 sm:mb-4 flex flex-wrap gap-2">
                    {selectedFiles.map((file, index) => (
                      <div
                        key={index}
                        className="flex items-center gap-1.5 sm:gap-2 px-2 sm:px-3 md:px-4 py-1.5 sm:py-2 bg-gradient-to-r from-indigo-50 to-purple-50 rounded-lg sm:rounded-xl border-2 border-indigo-200"
                      >
                        <FileText className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-indigo-600 flex-shrink-0" />
                        <span className="text-xs sm:text-sm font-medium text-gray-700 truncate max-w-[150px] sm:max-w-none">{file.name}</span>
                        <button
                          onClick={() => handleRemoveFile(index)}
                          className="text-red-600 hover:text-red-700 transition-colors flex-shrink-0"
                        >
                          <XCircle className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                <div className="flex items-end gap-2 sm:gap-3">
                  <label className="cursor-pointer p-2 sm:p-3 hover:bg-gray-100 rounded-lg sm:rounded-xl transition-colors border-2 border-gray-200 flex-shrink-0">
                    <Paperclip className="w-4 h-4 sm:w-5 sm:h-5 text-gray-600" />
                    <input
                      type="file"
                      multiple
                      onChange={handleFileSelect}
                      className="hidden"
                    />
                  </label>
                  <textarea
                    value={messageText}
                    onChange={(e) => setMessageText(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleSendMessage();
                      }
                    }}
                    placeholder="Type your message... (Press Enter to send, Shift+Enter for new line)"
                    rows={3}
                    className="flex-1 px-3 sm:px-4 py-2 sm:py-3 border-2 border-gray-300 rounded-lg sm:rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-none font-medium text-sm sm:text-base"
                  />
                  <button
                    onClick={handleSendMessage}
                    disabled={sendingMessage || (!messageText.trim() && selectedFiles.length === 0)}
                    className="px-4 sm:px-6 md:px-8 py-2 sm:py-2.5 md:py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-lg sm:rounded-xl font-bold hover:from-indigo-700 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5 sm:gap-2 shadow-md sm:shadow-lg hover:shadow-xl transition-all transform hover:scale-105 text-xs sm:text-sm md:text-base"
                    title={!messageText.trim() && selectedFiles.length === 0 ? 'Please enter a message or attach files' : ''}
                  >
                    {sendingMessage ? (
                      <Loader2 className="w-4 h-4 sm:w-5 sm:h-5 animate-spin" />
                    ) : (
                      <Send className="w-4 h-4 sm:w-5 sm:h-5" />
                    )}
                    <span className="hidden sm:inline">Send</span>
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Enhanced Timeline Tab */}
          {activeTab === 'timeline' && (
            <div className="p-3 sm:p-4 md:p-6">
              <div className="space-y-3 sm:space-y-4">
                {timeline.length === 0 ? (
                  <div className="text-center py-16 text-gray-500">
                    <Clock className="w-16 h-16 mx-auto mb-4 text-gray-400" />
                    <p className="font-semibold text-lg">No timeline events yet</p>
                  </div>
                ) : (
                  <div className="relative">
                    {/* Timeline Line */}
                    <div className="absolute left-4 sm:left-6 md:left-8 top-0 bottom-0 w-0.5 bg-gradient-to-b from-indigo-200 via-purple-200 to-pink-200"></div>
                    
                    {timeline.map((event, index) => {
                      const EventIcon = getEventIcon(event.type);
                      const isPriorityChange = event.type === 'PRIORITY_CHANGED';
                      const isComment = event.type === 'COMMENT_ADDED';
                      
                      return (
                        <div key={event.id} className="relative flex gap-3 sm:gap-4 md:gap-6 mb-4 sm:mb-5 md:mb-6">
                          {/* Icon */}
                          <div className={`relative z-10 flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 md:w-16 md:h-16 rounded-full flex items-center justify-center shadow-md sm:shadow-lg ${
                            isPriorityChange ? 'bg-gradient-to-br from-orange-400 to-red-500' :
                            isComment ? 'bg-gradient-to-br from-blue-400 to-indigo-500' :
                            'bg-gradient-to-br from-indigo-400 to-purple-500'
                          }`}>
                            <EventIcon className="w-5 h-5 sm:w-6 sm:h-6 md:w-8 md:h-8 text-white" />
                          </div>
                          
                          {/* Content */}
                          <div className="flex-1 bg-white rounded-lg sm:rounded-xl p-3 sm:p-4 md:p-5 shadow-md sm:shadow-lg border-2 border-gray-200 hover:border-indigo-300 transition-all min-w-0">
                            <div className="flex items-start justify-between mb-1.5 sm:mb-2">
                              <div className="flex-1 min-w-0">
                                <h4 className="font-bold text-gray-900 mb-1 text-xs sm:text-sm md:text-base">
                                  {formatTimelineEvent(event)}
                                </h4>
                                {event.actor && (
                                  <div className="flex items-center gap-1.5 sm:gap-2 text-xs sm:text-sm text-gray-600 mb-1.5 sm:mb-2 flex-wrap">
                                    <User className="w-3.5 h-3.5 sm:w-4 sm:h-4 flex-shrink-0" />
                                    <span className="font-medium truncate">
                                      {event.actor.name || event.actor.actor_id || 'System'}
                                    </span>
                                    {event.actor.actor_type && (
                                      <span className="px-1.5 sm:px-2 py-0.5 bg-gray-100 rounded text-[10px] sm:text-xs">
                                        {event.actor.actor_type}
                                      </span>
                                    )}
                                  </div>
                                )}
                                {event.created_at && (
                                  <div className="flex items-center gap-1.5 sm:gap-2 text-[10px] sm:text-xs text-gray-500">
                                    <Clock className="w-3 h-3 sm:w-3.5 sm:h-3.5 flex-shrink-0" />
                                    <span>{formatInIST(event.created_at)}</span>
                                  </div>
                                )}
                              </div>
                            </div>
                            
                            {/* Event Payload Details */}
                            {event.payload && Object.keys(event.payload).length > 0 && (
                              <div className="mt-3 sm:mt-4 pt-3 sm:pt-4 border-t border-gray-200">
                                {isPriorityChange && event.payload.previousPriority && event.payload.newPriority && (
                                  <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
                                    <span className="px-2 sm:px-3 py-0.5 sm:py-1 bg-red-100 text-red-800 rounded text-[10px] sm:text-xs font-bold">
                                      {event.payload.previousPriority}
                                    </span>
                                    <ChevronRight className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-gray-400 flex-shrink-0" />
                                    <span className="px-2 sm:px-3 py-0.5 sm:py-1 bg-green-100 text-green-800 rounded text-[10px] sm:text-xs font-bold">
                                      {event.payload.newPriority}
                                    </span>
                                    {event.payload.reason && (
                                      <span className="ml-auto text-[10px] sm:text-xs text-gray-500">
                                        Reason: {event.payload.reason.replace(/_/g, ' ')}
                                      </span>
                                    )}
                                  </div>
                                )}
                                {isComment && event.payload.attachmentCount > 0 && (
                                  <div className="flex items-center gap-1.5 sm:gap-2 text-xs sm:text-sm text-gray-600">
                                    <Paperclip className="w-3.5 h-3.5 sm:w-4 sm:h-4 flex-shrink-0" />
                                    <span>{event.payload.attachmentCount} attachment(s)</span>
                                  </div>
                                )}
                                {event.payload.ticketAgeMinutes && (
                                  <div className="text-[10px] sm:text-xs text-gray-500 mt-1.5 sm:mt-2">
                                    Ticket age: {event.payload.ticketAgeMinutes} minutes
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Enhanced Attachments Tab */}
          {activeTab === 'attachments' && (
            <div className="p-3 sm:p-4 md:p-6">
              {(!ticket.attachments || ticket.attachments.length === 0) ? (
                <div className="text-center py-12 sm:py-16 text-gray-500">
                  <Paperclip className="w-12 h-12 sm:w-16 sm:h-16 mx-auto mb-3 sm:mb-4 text-gray-400" />
                  <p className="font-semibold text-sm sm:text-base md:text-lg">No attachments</p>
                  <p className="text-xs sm:text-sm mt-2">Attach files to provide more context</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                  {ticket.attachments.map((attachment) => (
                    <div
                      key={attachment.attachment_id || attachment.id}
                      className="border-2 border-gray-200 rounded-lg sm:rounded-xl p-3 sm:p-4 md:p-5 hover:shadow-lg sm:hover:shadow-xl hover:border-indigo-300 transition-all bg-gradient-to-br from-white to-gray-50"
                    >
                      <div className="flex items-start justify-between mb-2 sm:mb-3">
                        <div className="p-2 sm:p-3 bg-gradient-to-br from-indigo-100 to-purple-100 rounded-lg sm:rounded-xl">
                          <FileText className="w-6 h-6 sm:w-8 sm:h-8 text-indigo-600" />
                        </div>
                        <button
                          onClick={() =>
                            handleDownloadAttachment(
                              attachment.attachment_id || attachment.id,
                              attachment.file_name
                            )
                          }
                          className="p-1.5 sm:p-2 hover:bg-indigo-100 rounded-lg transition-colors flex-shrink-0"
                        >
                          <Download className="w-4 h-4 sm:w-5 sm:h-5 text-indigo-600" />
                        </button>
                      </div>
                      <p className="text-xs sm:text-sm font-bold text-gray-900 mb-1.5 sm:mb-2 truncate">
                        {attachment.file_name}
                      </p>
                      <div className="flex items-center justify-between text-[10px] sm:text-xs text-gray-500">
                        <span>
                          {attachment.size_bytes
                            ? `${(attachment.size_bytes / 1024).toFixed(2)} KB`
                            : 'Unknown size'}
                        </span>
                        {attachment.uploaded_at && (
                          <span className="truncate ml-2">{formatInIST(attachment.uploaded_at)}</span>
                        )}
                      </div>
                      {attachment.uploaded_by && (
                        <div className="mt-2 pt-2 border-t border-gray-200">
                          <p className="text-[10px] sm:text-xs text-gray-500 truncate">
                            Uploaded by: {attachment.uploaded_by.name || attachment.uploaded_by.actor_id}
                          </p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Close Ticket Modal */}
      {showCloseModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-black/60 backdrop-blur-sm">
          <div className="bg-white rounded-lg sm:rounded-xl md:rounded-2xl shadow-2xl max-w-lg w-full p-4 sm:p-5 md:p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4 sm:mb-5 md:mb-6">
              <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-900">Close Ticket</h3>
              <button
                onClick={() => {
                  setShowCloseModal(false);
                  setCloseReason('');
                  setResolutionNote('');
                }}
                className="p-1.5 sm:p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <XCircle className="w-4 h-4 sm:w-5 sm:h-5 text-gray-600" />
              </button>
            </div>
            <div className="space-y-3 sm:space-y-4">
              <div>
                <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1.5 sm:mb-2">
                  Closure Reason <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={closeReason}
                  onChange={(e) => setCloseReason(e.target.value)}
                  placeholder="e.g., Issue resolved, Customer satisfied"
                  className="w-full px-3 sm:px-4 py-2 sm:py-3 border-2 border-gray-300 rounded-lg sm:rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm sm:text-base"
                />
              </div>
              <div>
                <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1.5 sm:mb-2">
                  Resolution Note
                </label>
                <textarea
                  value={resolutionNote}
                  onChange={(e) => setResolutionNote(e.target.value)}
                  placeholder="Optional: Add details about how the issue was resolved..."
                  rows={4}
                  className="w-full px-3 sm:px-4 py-2 sm:py-3 border-2 border-gray-300 rounded-lg sm:rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none text-sm sm:text-base"
                />
              </div>
            </div>
            <div className="flex items-center gap-2 sm:gap-3 mt-4 sm:mt-5 md:mt-6">
              <button
                onClick={() => {
                  setShowCloseModal(false);
                  setCloseReason('');
                  setResolutionNote('');
                }}
                className="flex-1 px-3 sm:px-4 py-2 sm:py-3 border-2 border-gray-300 rounded-lg sm:rounded-xl font-semibold text-gray-700 hover:bg-gray-50 text-sm sm:text-base transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleCloseTicket}
                disabled={!closeReason.trim() || closingTicket}
                className="flex-1 px-3 sm:px-4 py-2 sm:py-3 bg-gradient-to-r from-red-600 to-red-700 text-white rounded-lg sm:rounded-xl font-semibold hover:from-red-700 hover:to-red-800 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-1.5 sm:gap-2 text-sm sm:text-base"
              >
                {closingTicket ? (
                  <Loader2 className="w-4 h-4 sm:w-5 sm:h-5 animate-spin" />
                ) : (
                  <>
                    <Lock className="w-4 h-4 sm:w-5 sm:h-5" />
                    <span className="hidden sm:inline">Close Ticket</span>
                    <span className="sm:hidden">Close</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reopen Ticket Modal */}
      {showReopenModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-black/60 backdrop-blur-sm">
          <div className="bg-white rounded-lg sm:rounded-xl md:rounded-2xl shadow-2xl max-w-lg w-full p-4 sm:p-5 md:p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4 sm:mb-5 md:mb-6">
              <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-900">Reopen Ticket</h3>
              <button
                onClick={() => {
                  setShowReopenModal(false);
                  setReopenReason('');
                }}
                className="p-1.5 sm:p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <XCircle className="w-4 h-4 sm:w-5 sm:h-5 text-gray-600" />
              </button>
            </div>
            <div>
              <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1.5 sm:mb-2">
                Reopen Reason <span className="text-red-500">*</span>
              </label>
              <textarea
                value={reopenReason}
                onChange={(e) => setReopenReason(e.target.value)}
                placeholder="e.g., Issue not fully resolved, new information available"
                rows={4}
                className="w-full px-3 sm:px-4 py-2 sm:py-3 border-2 border-gray-300 rounded-lg sm:rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none text-sm sm:text-base"
              />
            </div>
            <div className="flex items-center gap-2 sm:gap-3 mt-4 sm:mt-5 md:mt-6">
              <button
                onClick={() => {
                  setShowReopenModal(false);
                  setReopenReason('');
                }}
                className="flex-1 px-3 sm:px-4 py-2 sm:py-3 border-2 border-gray-300 rounded-lg sm:rounded-xl font-semibold text-gray-700 hover:bg-gray-50 text-sm sm:text-base transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleReopenTicket}
                disabled={!reopenReason.trim() || reopeningTicket}
                className="flex-1 px-3 sm:px-4 py-2 sm:py-3 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-lg sm:rounded-xl font-semibold hover:from-green-700 hover:to-green-800 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-1.5 sm:gap-2 text-sm sm:text-base"
              >
                {reopeningTicket ? (
                  <Loader2 className="w-4 h-4 sm:w-5 sm:h-5 animate-spin" />
                ) : (
                  <>
                    <Unlock className="w-4 h-4 sm:w-5 sm:h-5" />
                    <span className="hidden sm:inline">Reopen Ticket</span>
                    <span className="sm:hidden">Reopen</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TicketDetails;
